import React, { useState, useCallback } from 'react'
import _ from 'lodash'

import {
  Button,
  TranslatedInput,
  ConfirmModal,
  EditorTransitionPrompt,
} from '../../../components'
import { DeleteIcon, SortUp, SortDown } from '../../../components/icons'
import useModel from '../../../common/useModel'
import required from '../../../common/validators/required'

import S from './styled'

const Unit = ({ label, name, text, handleTranslationChange, isRequired, defaultLocale, locales, model }) => (
  <TranslatedInput {...{
    name: `${name}.${defaultLocale}`,
    text,
    title: label,
    locales,
    defaultLocale,
    translatedField: name,
    translates: _.get(model, name, {}),
    onTranslationChange: handleTranslationChange,
    className: 'descriptionInput',
    ...(isRequired ? { validate: required } : {}),
  }}/>
)

const Item = ({
  item, locales, onDelete, noDescription, onSave, onCancelNew, isNew, withImage, textArea,
}) => {
  const { text, reset, model, resetDirty, onChange, values } = useModel({
    values: item,
  })

  const [showModal, setShowModal] = useState(false)
  const [collapsed, setCollapsed] = useState(!isNew)

  const defaultLocale = (locales[0] || { id: 0 }).id

  const namePrefix = path => `values.${path}`

  const handleTranslationChange = useCallback((field, translates) => {
    onChange({
      target: {
        value: translates,
        name: field,
      },
    })
  }, [onChange])

  if (collapsed) {
    return (
      <S.Item>
        <S.ItemContent onClick={() => setCollapsed(false)} className="collapsed">
          {
            isNew ? <S.IconPlaceholder /> : (
              <S.DeleteLink onClick={
                e => {
                  setShowModal(true)
                  e.stopPropagation()
                }
              } className="collapsed">
                <DeleteIcon />
              </S.DeleteLink>
            )
          }
          <S.Title>
            {
              values.displayName[Reflect.ownKeys(values.displayName)[0]]
            }
          </S.Title>
          <S.Collapse>
            <SortUp />
          </S.Collapse>
        </S.ItemContent>
        <ConfirmModal
          isOpened={showModal}
          onClose={() => setShowModal(null)}
          onConfirm={() => {
            setShowModal(null)
            onDelete(item.id, item)
          }}
          redConfirm
        >
          Are you sure you want to delete this item?
        </ConfirmModal>
      </S.Item>
    )
  }

  return (
    <S.Item>
      <EditorTransitionPrompt when={model.isDirty}/>
      <S.ItemContent>
        {
          isNew ? <S.IconPlaceholder /> : (
            <S.DeleteLink onClick={
              () => setShowModal(true)
            }>
              <DeleteIcon />
            </S.DeleteLink>
          )
        }

        <div>
          <TranslatedInput {...{
            name: `values.displayName.${defaultLocale}`,
            text,
            title: 'Display name',
            locales,
            defaultLocale,
            translatedField: 'values.displayName',
            translates: _.get(model, 'values.displayName', {}),
            onTranslationChange: handleTranslationChange,
            className: 'descriptionInput',
            validate: required,
          }}/>
        </div>

        <S.Locales>
          <Unit {...{
            label: 'Zero', name: namePrefix`zero`, text, handleTranslationChange,
            defaultLocale, locales, model,
          }}/>
          <Unit {...{
            label: 'One', name: namePrefix`one`, text, handleTranslationChange,
            defaultLocale, locales, model, isRequired: true,
          }}/>
          <Unit {...{
            label: 'Two', name: namePrefix`two`, text, handleTranslationChange,
            defaultLocale, locales, model, isRequired: false,
          }}/>
          <Unit {...{
            label: 'Few', name: namePrefix`few`, text, handleTranslationChange,
            defaultLocale, locales, model, isRequired: false,
          }}/>
          <Unit {...{
            label: 'Many', name: namePrefix`many`, text, handleTranslationChange,
            defaultLocale, locales, model, isRequired: true,
          }}/>
          <Unit {...{
            label: 'Other', name: namePrefix`other`, text, handleTranslationChange,
            defaultLocale, locales, model, isRequired: false,
          }}/>
        </S.Locales>
        {
          !isNew && (
            <S.Collapse onClick={() => setCollapsed(true)} className="collapseUp">
              <SortDown />
            </S.Collapse>
          )
        }
      </S.ItemContent>
      <S.ItemFooter>
        { isNew && (<Button blue outlined onClick={() => onCancelNew()}>Cancel</Button>) }
        { !isNew && model.isDirty && (<Button blue outlined onClick={() => reset('values')}>Reset</Button>) }
        <Button
          blue disabled={!model.isDirty || !model.isValid}
          onClick={() => onSave(model.values).then(resetDirty)}>
          {isNew ? 'Add' : 'Save'}
        </Button>
      </S.ItemFooter>
      <ConfirmModal
        isOpened={showModal}
        onClose={() => setShowModal(null)}
        onConfirm={() => {
          setShowModal(null)
          onDelete(item.id, item)
        }}
        redConfirm
      >
        Are you sure you want to delete this item?
      </ConfirmModal>
    </S.Item>
  )
}

export default Item
